import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { Inject, Injectable } from '@angular/core';
import { AssetAccountantSubscription } from '@assetaccountant/api/models/asset-accountant-subscription';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import {
  ANALYTICS_CONFIG,
  AnalyticsConfig,
} from '@assetaccountant/core/analytics/analytics-config';
import {
  AnalyticsProperties,
  AnalyticsVendors,
  AppcuesApi,
  HeapApi,
} from '@assetaccountant/core/analytics/analytics-vendors';
import { environment } from 'environments/environment';
import { take } from 'rxjs/operators';
import { AppNavigationService } from './app-navigation.service';
import { AuthenticationService } from './authentication.service';
import { InstrumentationService } from './instrumentation.service';
import { SubscriptionService } from './subscription.service';

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private isFirstNewOrg = false;
  private isTrackingInitialised = false;
  private organisation: OrganisationDto | null = null;
  private sessionReplayTracking: any;
  private heap: HeapApi | null = null;
  private appcues: Promise<AppcuesApi> | null = null;

  get isNotTestOrganisation() {
    // eslint-disable-next-line ize/ize
    return !this.organisation?.name.includes('organisation');
  }

  constructor(
    private instrumentation: InstrumentationService,
    private authService: AuthenticationService,
    private appNavigationService: AppNavigationService,
    private subscriptionService: SubscriptionService,
    private vendors: AnalyticsVendors,
    @Inject(ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig | null,
  ) {
    this.appNavigationService.onOrganisationChanged.subscribe(() => {
      setTimeout(() => {
        const organisation = this.appNavigationService.getActiveOrganisation();
        this.updateIdentity(organisation);
      }, 1000);
    });
  }

  /**
   * Identifies the signed-in user to Datadog RUM, Heap and Appcues, loading the Heap and Appcues
   * SDKs on first call. Called once the current user has loaded. Amplitude identifies separately
   * (by email) in initaliseUserTracking.
   */
  identifyUser() {
    const userId = this.authService.getCurrentLoggedOnUserId();
    if (!this.analyticsConfig || !userId) {
      return;
    }

    const email = this.authService.getCurrentUserEmail();
    const name = this.authService.getCurrentUserName();
    const { heap, appcues } = this.analyticsConfig;

    this.safely(() => {
      if (this.vendors.rum.getInitConfiguration()) {
        this.vendors.rum.setUser({ id: userId, email, name });
      }
    });

    if (heap) {
      this.safely(() => {
        this.heap ??= this.vendors.loadHeap(heap.appId);
        this.heap.identify(userId);
        this.heap.addUserProperties({ email, name });
      });
    }

    if (appcues) {
      this.appcues ??= this.vendors.loadAppcues(appcues.accountId);
      // Property names are case-sensitive in Appcues.
      // LeaseQuery populates `Email` and `Name`. Sending lowercase `email`/`name` maps to separate properties (shown as "[Do not Use] Email/Name", hidden), so segment conditions on `Email`
      // evaluate to null for FA users and targeting by email silently fails. Keep these capitalised.
      this.withAppcues(sdk => sdk.identify(userId, { Email: email, Name: name }));
    }
  }

  initaliseUserTracking(isFirstNewOrg = false) {
    if (this.isNotTestOrganisation && !this.isTrackingInitialised) {
      // '-' is the "intentionally unset" sentinel from the variable group —
      // amplitude.init with a bogus key would 400 every event, so just bail.
      const apiKey = environment.amplitude.apiKey;
      if (!apiKey || apiKey === '-') {
        return;
      }

      this.isFirstNewOrg = isFirstNewOrg;
      this.isTrackingInitialised = true;
      this.sessionReplayTracking = sessionReplayPlugin({ sampleRate: 1 });

      const userId = this.authService.getCurrentLoggedOnUserId();
      const userEmail = this.authService.getCurrentUserEmail();

      // amplitude.setUserId(userId);

      amplitude.init(apiKey, userEmail, {
        autocapture: {
          attribution: false,
          pageViews: false,
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
        },
      });

      if (!environment.production) {
        console.log('Initalising user tracking', {
          isFirstNewOrg,
          isTrackingInitialised: this.isTrackingInitialised,
          userEmail,
          userId,
        });
      }
    }
  }

  startSessionReplay() {
    if (!this.sessionReplayTracking) {
      return;
    }
    amplitude.add(this.sessionReplayTracking);
  }

  trackEvent(eventName: string, eventValue: string | null = null, data: any = null) {
    if (!environment.production && this.isNotTestOrganisation) {
      console.log('Tracking event', eventName, eventValue, data);
    }

    try {
      amplitude.track(eventName, { eventValue, ...data });
      this.heap?.track(eventName, toFlatProperties({ eventValue, ...data }));
    } catch (e) {
      this.instrumentation.logError(e);
    }
  }

  updateIdentity(organisation: OrganisationDto | null) {
    this.organisation = organisation;

    if (!this.isNotTestOrganisation || !organisation?.organisationId) {
      return;
    }

    // Don't update the Amplitude identity if this is the first new organisation as it interrupts the session replay
    const updateAmplitude = !this.isFirstNewOrg;
    if (!updateAmplitude && !this.analyticsConfig) {
      return;
    }

    try {
      if (updateAmplitude) {
        amplitude.reset();
      }

      this.subscriptionService
        .getSubscription(organisation.organisationId)
        .pipe(take(1))
        .subscribe(subscription => {
          if (updateAmplitude) {
            const identifyEvent = new amplitude.Identify();
            identifyEvent.set('OrganisationId', organisation?.organisationId ?? '');
            identifyEvent.set('OrganisationName', organisation?.name ?? '');
            identifyEvent.set('Package Name', subscription.packageName);
            identifyEvent.set('Is Managed', subscription.isManaged);

            amplitude.identify(identifyEvent);
          }

          this.identifyOrganisation(organisation, subscription);
        });

      if (!environment.production) {
        console.log('Updating identity', {
          organisationId: organisation?.organisationId,
          organisationName: organisation?.name,
        });
      }
    } catch (e) {
      this.instrumentation.logError(e);
    }
  }

  private _referralSource?: string;
  trackMarketingSource(referralSource: string) {
    this._referralSource = referralSource;
  }

  getMarketingSource() {
    return this._referralSource;
  }

  /** Pushes the active organisation to Datadog RUM, Heap (as the account) and Appcues (as the group). */
  private identifyOrganisation(
    organisation: OrganisationDto,
    subscription: AssetAccountantSubscription,
  ) {
    if (!this.analyticsConfig) {
      return;
    }

    const organisationId = organisation.organisationId;
    const organisationProperties: AnalyticsProperties = {
      OrganisationId: organisationId,
      OrganisationName: organisation.name,
      'Package Name': subscription.packageName ?? '',
      'Is Managed': subscription.isManaged,
    };

    this.safely(() => {
      if (this.vendors.rum.getInitConfiguration()) {
        // eslint-disable-next-line ize/ize
        this.vendors.rum.setGlobalContextProperty('organisation', {
          id: organisationId,
          name: organisation.name,
          packageName: subscription.packageName,
          isManaged: subscription.isManaged,
        });
      }
    });

    this.safely(() => {
      this.heap?.addUserProperties({
        OrganisationId: organisationId,
        OrganisationName: organisation.name,
      });
      this.heap?.addAccountProperties(organisationProperties);
    });

    this.withAppcues(sdk => sdk.group(String(organisationId), organisationProperties));
  }

  private withAppcues(action: (sdk: AppcuesApi) => void) {
    this.appcues?.then(action).catch(e => this.instrumentation.logError(e));
  }

  private safely(action: () => void) {
    try {
      action();
    } catch (e) {
      this.instrumentation.logError(e);
    }
  }
}

/** Heap event properties must be flat primitives, so nested values (e.g. command objects) are dropped. */
function toFlatProperties(data: Record<string, unknown>): AnalyticsProperties {
  const properties: AnalyticsProperties = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      properties[key] = value;
    }
  }
  return properties;
}
